import { useState, useEffect, useRef } from 'react'
import { usePageMeta } from '../../hooks/usePageMeta'
import { Article, PaginatedResponse } from '../../types'
import api from '../../services/api'
import Loading from '../../components/common/Loading'
import ErrorMessage from '../../components/common/ErrorMessage'
import ArticleCard from './components/ArticleCard'
import Pagination from './components/Pagination'
import './News.css'

type ArticlesResponse = PaginatedResponse<Article>

function NewsList() {
  usePageMeta({
    title: 'Actualités',
    description: 'Suivez toute l\'actualité de notre campagne.',
  })

  const [articles, setArticles] = useState<Article[]>([])
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false,
  })
  const [currentPage, setCurrentPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Use ref to track abort controller
  const abortControllerRef = useRef<AbortController | null>(null)

  useEffect(() => {
    const fetchArticles = async () => {
      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController()
      const signal = abortControllerRef.current.signal

      setLoading(true)
      setError(null)

      try {
        const response = await api.get<ArticlesResponse>(
          `/articles?page=${currentPage}&limit=10`,
          { signal } // Pass signal to abort
        )

        // Only update state if not aborted
        if (!signal.aborted) {
          setArticles(response.data)
          setPagination(response.pagination)
        }
      } catch (err) {
        // Ignore abort errors
        if (err instanceof Error && err.name === 'AbortError') {
          return
        }

        if (!signal.aborted) {
          setError(err instanceof Error ? err.message : 'Erreur lors du chargement des articles')
        }
      } finally {
        if (!signal.aborted) {
          setLoading(false)
        }
      }
    }

    fetchArticles()

    // Cleanup: abort on unmount or page change
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [currentPage])

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Show loading state on initial load
  if (loading && articles.length === 0) {
    return <Loading />
  }

  // Show error state
  if (error) {
    return (
      <div className="news-page">
        <div className="container">
          <ErrorMessage message={error} />
        </div>
      </div>
    )
  }

  return (
    <div className="news-page">
      <div className="container">
        <header className="news-header">
          <h1>Actualités</h1>
          <p className="news-subtitle">
            Suivez toute l'actualité de notre campagne
          </p>
        </header>

        {/* Show loading overlay during pagination */}
        {loading && articles.length > 0 && (
          <div style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
          }}>
            <Loading />
          </div>
        )}

        {/* Articles Grid */}
        {articles.length > 0 && (
          <>
            <div className="articles-grid">
              {articles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={pagination.total_pages}
                onPageChange={handlePageChange}
              />
            )}
          </>
        )}

        {/* Empty State */}
        {articles.length === 0 && !loading && (
          <div className="no-articles">
            <p>Aucune actualité disponible pour le moment.</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default NewsList
